import { useMemo, useState } from 'react';
import Carregando from '@/components/Carregando';
import FiltrosBarra from '@/components/leads/FiltrosBarra';
import PainelLead from '@/components/leads/PainelLead';
import DialogoNovoLead from '@/components/leads/DialogoNovoLead';
import DialogoEtapa from '@/components/leads/DialogoEtapa';
import TabelaLeads, { chaveDeOrdenacao, exportarCsv } from '@/components/leads/TabelaLeads';
import { useLeads } from '@/leads/LeadsProvider';
import { ETAPAS_KANBAN, etapaPorId } from '@/lib/constants';
import { apenasSdrs } from '@/equipe/funcoes';
import { atribuirEmMassa, moverEtapaEmMassa } from '@/services/leadsService';
import { aplicarFiltros, janelaDoPeriodo } from '@/leads/filtros';
import { useTarefasAbertas } from '@/leads/useTarefasAbertas';
import { useAuth } from '@/auth/AuthProvider';
import { ehGestor } from '@/leads/posseTela';

/**
 * ★ VI — a tabela (12 colunas do dono, CSV igual) mora em `components/leads/TabelaLeads.jsx`;
 * aqui ficam a seleção, a ordenação e as ações em massa.
 */
export default function LeadsPage() {
  const { papel } = useAuth();
  const gestorDaTela = ehGestor(papel);
  const { filtros, leads: todosOsLeads, carregando, erro, equipe, clinicaId, autor, abrirLead, leadAberto, clinica} =
    useLeads();
  // ★ PC-4: a Lista filtra AQUI (não usa o `leadsFiltrados` do provider), porque "Sem próximo
  // contato" precisa das tarefas abertas, que só o Funil e a Lista assinam.
  const tarefas = useTarefasAbertas(clinicaId);
  const leadsFiltrados = useMemo(
    () => aplicarFiltros(todosOsLeads, filtros, { comAberta: tarefas.comAberta }),
    [todosOsLeads, filtros, tarefas.comAberta],
  );
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [ordem, setOrdem] = useState({ campo: 'criadoEm', desc: true });
  const [novoAberto, setNovoAberto] = useState(false);
  const [pendente, setPendente] = useState(null);
  const [erroAcao, setErroAcao] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const ordenados = useMemo(() => {
    const copia = [...leadsFiltrados];
    const { campo, desc } = ordem;
    const opcoes = { equipe, config: clinica?.config ?? null };
    copia.sort((a, b) => {
      const x = chaveDeOrdenacao(a, campo, opcoes);
      const y = chaveDeOrdenacao(b, campo, opcoes);
      if (x < y) return desc ? 1 : -1;
      if (x > y) return desc ? -1 : 1;
      return 0;
    });
    return copia;
  }, [leadsFiltrados, ordem, equipe, clinica]);

  // A seleção segue o recorte atual: filtrar não deve deixar leads invisíveis
  // dentro de uma ação em massa.
  const visiveis = useMemo(() => new Set(ordenados.map((l) => l.id)), [ordenados]);
  const selecionadosVisiveis = useMemo(
    () => ordenados.filter((l) => selecionados.has(l.id)),
    [ordenados, selecionados],
  );
  const todosMarcados = ordenados.length > 0 && selecionadosVisiveis.length === ordenados.length;

  function alternar(id) {
    setSelecionados((atual) => {
      const proximo = new Set(atual);
      proximo.has(id) ? proximo.delete(id) : proximo.add(id);
      return proximo;
    });
  }

  function alternarTodos() {
    setSelecionados(todosMarcados ? new Set() : new Set(visiveis));
  }

  function ordenarPor(campo) {
    setOrdem((o) => ({ campo, desc: o.campo === campo ? !o.desc : true }));
  }

  async function emMassa(acao) {
    setErroAcao(null);
    setOcupado(true);
    try {
      await acao(selecionadosVisiveis);
      setSelecionados(new Set());
    } catch (e) {
      setErroAcao(e.message ?? 'Não foi possível concluir a ação em massa.');
    } finally {
      setOcupado(false);
    }
  }

  if (carregando) return <Carregando texto="Carregando os leads…" />;

  return (
    <div className={leadAberto ? 'pagina com-painel' : 'pagina'}>
      <div className="pagina-principal">
        <header className="pagina-cabecalho com-acao">
          <h1>Leads</h1>
          <button
            type="button"
            className="btn-primario compacto"
            onClick={() => setNovoAberto(true)}
          >
            Novo lead
          </button>
        </header>

        <FiltrosBarra filtrados={leadsFiltrados} tarefas={tarefas} />

        {erro && <p className="erro">{erro}</p>}
        {erroAcao && <p className="erro">{erroAcao}</p>}

        {selecionadosVisiveis.length > 0 && (
          <div className="barra-massa">
            <strong>{selecionadosVisiveis.length} selecionados</strong>

            {/* ★★★ PC-6: "Atribuir a…" é só do GESTOR, e vai pela callable `assumirLead` (blocos de 100,
                trava + tarefas + trilha). A SDR assume um lead por vez, na ficha. */}
            {gestorDaTela && (
              <select
                defaultValue=""
                disabled={ocupado}
                aria-label="Atribuir a"
                onChange={(e) => {
                  const sdr = equipe.find((u) => u.id === e.target.value);
                  e.target.value = '';
                  if (sdr) emMassa((leads) => atribuirEmMassa(clinicaId, leads, sdr, autor));
                }}
              >
                <option value="">Atribuir a…</option>
                {apenasSdrs(equipe).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
              </select>
            )}

            <select
              defaultValue=""
              disabled={ocupado}
              aria-label="Mover para etapa"
              onChange={(e) => {
                const etapa = e.target.value;
                e.target.value = '';
                if (etapa) emMassa((leads) => moverEtapaEmMassa(clinicaId, leads, etapa, autor));
              }}
            >
              <option value="">Mover para…</option>
              {/* Fechou e as duas de "não" exigem valor/motivo POR LEAD — só pelo
                  painel. `moverEtapaEmMassa` recusaria; não oferecer é melhor. */}
              {ETAPAS_KANBAN.filter((e) => !['fechado', 'nao_agendou', 'nao_fechou'].includes(e)).map((id) => (
                <option key={id} value={id}>
                  {etapaPorId(id).label}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="btn-secundario"
              onClick={() => exportarCsv(selecionadosVisiveis, { equipe, config: clinica?.config ?? null })}
            >
              Exportar CSV
            </button>

            <button type="button" className="btn-texto" onClick={() => setSelecionados(new Set())}>
              Limpar seleção
            </button>
          </div>
        )}

        <TabelaLeads
          leads={ordenados}
          janela={janelaDoPeriodo(filtros.periodo)}
          equipe={equipe}
          config={clinica?.config ?? null}
          ordem={ordem}
          aoOrdenar={ordenarPor}
          selecionados={selecionados}
          aoAlternar={alternar}
          todosMarcados={todosMarcados}
          aoAlternarTodos={alternarTodos}
          aoAbrir={abrirLead}
        />
      </div>

      <PainelLead aoPedirEtapa={setPendente} />
      <DialogoNovoLead aberto={novoAberto} aoFechar={() => setNovoAberto(false)} />
      <DialogoEtapa
        grade={clinica?.config?.agendaAvaliacao}
        config={clinica?.config ?? null}
        pendente={pendente}
        aoFechar={() => setPendente(null)}
        clinicaId={clinicaId}
        autor={autor}
        equipe={equipe}
      />
    </div>
  );
}
